import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { match } from "ts-pattern";

import type {
  OrganisationId,
  UserId,
  WaitingWork,
} from "@thalamos/haven-common";

import type { ErrorBody } from "../api/axios/api.js";
import { apiHooks } from "../api/index.js";
import { useUser } from "./UserContext.js";

/** Where listing what waits on the signed-in user has got. */
export type WaitingWorkListing =
  | { kind: "unlisted" }
  | { kind: "listing" }
  | { kind: "listed"; waiting: WaitingWork }
  | {
      kind: "stale";
      /** The latest listing failed; this is the one before it. */
      waiting: WaitingWork;
    }
  | { kind: "failed" };

/** The shell's listing of what waits, and a way to list it again. */
export type SharedWaitingWork = {
  listing: WaitingWorkListing;
  /** Never rejects: a failure shows in `listing`. */
  list: () => Promise<void>;
};

/**
 * Where a listing has got, from what the request last said. An error that
 * still holds the last good result is `stale`, so that result stays shown.
 */
export const listingOf = ({
  data,
  response,
  error,
  loading,
}: Readonly<{
  data?: WaitingWork | ErrorBody;
  response?: { status: number };
  error: unknown;
  loading: boolean;
}>): WaitingWorkListing => {
  const lastListed =
    response?.status === 200 && data !== undefined && "groups" in data
      ? data
      : undefined;

  if (error !== null && error !== undefined) {
    return lastListed === undefined
      ? { kind: "failed" }
      : { kind: "stale", waiting: lastListed };
  }

  if (response !== undefined && response.status !== 200) {
    return { kind: "failed" };
  }

  if (lastListed !== undefined) {
    return { kind: "listed", waiting: lastListed };
  }

  return loading ? { kind: "listing" } : { kind: "unlisted" };
};

/** What a listing has to show, out of date or not. */
export const waitingShownBy = (
  listing: WaitingWorkListing,
): WaitingWork | undefined =>
  match(listing)
    .with({ kind: "listed" }, { kind: "stale" }, ({ waiting }) => waiting)
    .with(
      { kind: "unlisted" },
      { kind: "listing" },
      { kind: "failed" },
      () => undefined,
    )
    .exhaustive();

export const WaitingWorkContext = createContext<SharedWaitingWork | undefined>(
  undefined,
);

/**
 * Lists what waits on the signed-in user, once for the shell and whatever page
 * it holds. Only case work has anything waiting on it. Listed again on every
 * move between pages, because acting on something always moves, and what
 * changed is not always a young person this listing already names; and on
 * moving to act elsewhere, which can leave the page where it was.
 */
export const useListWaitingWork = (): SharedWaitingWork => {
  const { user } = useUser();
  const sessionContext = user?.sessionContext ?? null;
  const caseWork = sessionContext?.kind === "case-work";
  const userId = user?.id;
  const actingOrganisationId = sessionContext?.organisationId;
  const { pathname } = useLocation();

  const [requested, refetch] = apiHooks.waitingWork.list();
  // The request holds on to what it last listed, which can be for a user or
  // organisation the session has since left.
  const [listedFor, setListedFor] = useState<{
    userId: UserId | undefined;
    organisationId: OrganisationId | undefined;
  }>();

  const list = useCallback(
    () =>
      refetch().then(
        () => undefined,
        () => undefined,
      ),
    [refetch],
  );

  useEffect(() => {
    if (caseWork) {
      // Not `list`, which cannot say whom it listed for.
      const listingFor = { userId, organisationId: actingOrganisationId };
      void refetch().then(
        () => {
          // The same object again when nothing changed, so a listing for the
          // same session does not render again.
          setListedFor((before) =>
            before !== undefined &&
            before.userId === listingFor.userId &&
            before.organisationId === listingFor.organisationId
              ? before
              : listingFor,
          );
        },
        () => undefined,
      );
    }
  }, [caseWork, userId, actingOrganisationId, pathname, refetch]);

  const listedForThisSession =
    listedFor?.userId === userId &&
    listedFor?.organisationId === actingOrganisationId;

  return {
    listing: caseWork
      ? listingOf(
          listedForThisSession
            ? requested
            : { error: requested.error, loading: requested.loading },
        )
      : { kind: "unlisted" },
    list,
  };
};

/** What the shell last listed of the signed-in user's waiting work. */
export const useWaitingWork = (): SharedWaitingWork => {
  const context = useContext(WaitingWorkContext);
  if (context === undefined) {
    throw new Error("useWaitingWork must be used within MainLayout");
  }
  return context;
};
